import { useAgents } from "@/hooks/useAgents";
import { cn } from "@/lib/utils";
import { ChevronDown, Search } from "lucide-react";
import * as React from "react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Input } from "../ui/input";

interface AgentFilterProps {
  value: string | string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  availableAgents?: { microsoftId: string; name: string; }[];
  className?: string;
}

export const AgentFilter: React.FC<AgentFilterProps> = ({
  value,
  onChange,
  placeholder = "All Agents",
  availableAgents,
  className
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState("");
  const { agents: displayAgents, loading, error } = useAgents();

  // Use availableAgents if provided, otherwise use all agents
  // Ensure value is always an array
  const selectedValues = React.useMemo(() => {
    if (!value) return [""];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // Auto-reset to "All Agents" if selected agent is removed from the list
  React.useEffect(() => {
    if (!selectedValues || selectedValues.length === 0 || selectedValues.includes("")) {
      return; // Already showing "All Agents"
    }
    if (loading) return; // Don't reset while agents are loading

    const availableAgentIds = displayAgents.map(agent => agent.microsoftId);
    const hasRemovedAgent = selectedValues.some(id => id !== "" && !availableAgentIds.includes(id));

    if (hasRemovedAgent) {
      console.log('AgentFilter: Detected removed agent in selection, resetting to All Agents');
      onChange([]); // Reset to "All Agents"
    }
  }, [displayAgents, selectedValues, onChange, loading]);

  // Filter agents based on search term
  const filteredAgents = React.useMemo(() => {
    if (!searchTerm.trim()) return displayAgents;
    return displayAgents.filter(agent =>
      agent.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [displayAgents, searchTerm]);

  const handleValueToggle = (microsoftId: string) => {
    if (microsoftId === "") {
      onChange([]);
      return;
    }

    const newValue = selectedValues.includes(microsoftId)
      ? selectedValues.filter((v) => v !== microsoftId && v !== "")
      : [...selectedValues.filter((v) => v !== ""), microsoftId];

    onChange(newValue.length === 0 ? [] : newValue);
  };

  const handleOpenChange = (newOpen: boolean) => {
    // Only allow closing when clicking outside
    if (!newOpen) {
      setOpen(false);
      setSearchTerm(""); // Clear search when closing
    } else {
      setOpen(true);
    }
  };

  const selectedLabels = selectedValues
    .map((v) => displayAgents.find((a) => a.microsoftId === v)?.name)
    .filter(Boolean)
    .join(", ");

  return (
    <div className="flex items-center gap-2 w-full min-w-[180px] md:w-[230px]">
      <DropdownMenu open={open} onOpenChange={handleOpenChange} modal={false}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn("relative flex items-center w-full bg-[#F5F6F7] dark:bg-[#0c3451] rounded-xl px-4 py-1.5 font-bold text-primary dark:text-white text-sm focus:outline-none border-0 shadow-none h-[36px] hover:bg-[#e9eaeb] dark:hover:bg-[#2C4C63] transition justify-between", className)}
            style={{ boxShadow: "none" }}
          >
            <span className="truncate text-left text-sm w-full pr-6 text-primary dark:text-white">{selectedLabels || placeholder}</span>
            <ChevronDown className="w-4 h-4 text-primary dark:text-white absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[300px] p-0 text-sm bg-white dark:bg-[#3C5C73] max-h-[300px] overflow-hidden" align="start">
          {/* Search Input */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search agents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 text-sm dark:bg-[#3C5C73] border-gray-200 dark:border-white text-primary dark:text-white placeholder-gray-500"
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
              />
            </div>
          </div>

          {/* Scrollable Options */}
          <div className="max-h-[200px] overflow-y-auto">
            <div className="p-2">
              <DropdownMenuCheckboxItem
                key="all"
                checked={selectedValues?.length === 0}
                onCheckedChange={() => handleValueToggle("")}
                onSelect={(e) => {
                  e.preventDefault();
                }}
                className="text-sm text-primary dark:text-white hover:bg-[#f5f6f7] dark:hover:bg-[#2C4C63] cursor-pointer"
              >
                All Agents
              </DropdownMenuCheckboxItem>
              {filteredAgents.length === 0 && searchTerm.trim() ? (
                <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  No agents found
                </div>
              ) : (
                filteredAgents.map((agent, index) => (
                  <DropdownMenuCheckboxItem
                    key={`agent-${agent.microsoftId}-${index}`}
                    checked={selectedValues.includes(agent.microsoftId)}
                    onCheckedChange={() => handleValueToggle(agent.microsoftId)}
                    onSelect={(e) => {
                      e.preventDefault();
                    }}
                    className="text-sm text-primary dark:text-white hover:bg-[#f5f6f7] dark:hover:bg-[#2C4C63] cursor-pointer"
                  >
                    {agent.name}
                  </DropdownMenuCheckboxItem>
                ))
              )}
            </div>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
